// Centralized Google Maps API loader to prevent multiple inclusions
let isGoogleMapsLoaded = false;
let isGoogleMapsLoading = false;
let loadPromise = null;
let loadCallbacks = [];

const GOOGLE_MAPS_API_KEY = "AIzaSyAOOAHADllUMGULOz5FQu3rIhM0RtwxP7Q";

/**
 * Load Google Maps API only once
 * @param {string} libraries - Comma-separated list of libraries to load (e.g., 'places,geometry')
 * @returns {Promise} Promise that resolves when Google Maps API is loaded
 */
export function loadGoogleMapsAPI(libraries = "places") {
  // If already loaded, return resolved promise
  if (isGoogleMapsLoaded && window.google && window.google.maps) {
    return Promise.resolve(window.google);
  }

  // If already loading, return existing promise
  if (isGoogleMapsLoading && loadPromise) {
    return loadPromise;
  }

  // Check if Google Maps script is already in the page
  const existingScript =
    document.querySelector('script[src*="maps.google.com/maps/api/js"]') ||
    document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

  if (existingScript && window.google && window.google.maps) {
    isGoogleMapsLoaded = true;
    return Promise.resolve(window.google);
  }

  // If there's an existing script but Google is not loaded yet, wait for it
  if (existingScript && !window.google) {
    isGoogleMapsLoading = true;
    loadPromise = new Promise((resolve, reject) => {
      const checkGoogleLoaded = () => {
        if (window.google && window.google.maps) {
          isGoogleMapsLoaded = true;
          isGoogleMapsLoading = false;
          resolve(window.google);
        } else {
          setTimeout(checkGoogleLoaded, 100);
        }
      };
      checkGoogleLoaded();

      // Timeout after 10 seconds
      setTimeout(() => {
        if (!isGoogleMapsLoaded) {
          isGoogleMapsLoading = false;
          reject(new Error("Google Maps API failed to load within timeout"));
        }
      }, 10000);
    });
    return loadPromise;
  }

  // Create new script to load Google Maps
  isGoogleMapsLoading = true;
  loadPromise = new Promise((resolve, reject) => {
    try {
      // Remove any existing incomplete scripts
      const incompleteScripts = document.querySelectorAll(
        'script[src*="maps.google.com/maps/api/js"]:not([data-loaded="true"])'
      );
      incompleteScripts.forEach((script) => script.remove());

      const script = document.createElement("script");
      const src = `https://maps.google.com/maps/api/js?libraries=${libraries}&key=${GOOGLE_MAPS_API_KEY}`;

      script.type = "text/javascript";
      script.src = src;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        script.setAttribute("data-loaded", "true");
        if (window.google && window.google.maps) {
          isGoogleMapsLoaded = true;
          isGoogleMapsLoading = false;
          resolve(window.google);
        } else {
          // Sometimes the API takes a moment to initialize
          let retries = 0;
          const checkReady = () => {
            if (window.google && window.google.maps) {
              isGoogleMapsLoaded = true;
              isGoogleMapsLoading = false;
              resolve(window.google);
            } else if (retries < 50) {
              // 5 seconds max
              retries++;
              setTimeout(checkReady, 100);
            } else {
              isGoogleMapsLoading = false;
              reject(new Error("Google Maps API loaded but google.maps is not available"));
            }
          };
          checkReady();
        }
      };

      script.onerror = () => {
        isGoogleMapsLoading = false;
        script.remove();
        reject(new Error("Failed to load Google Maps API script"));
      };

      // Add to head
      document.head.appendChild(script);

      // Timeout after 10 seconds
      setTimeout(() => {
        if (!isGoogleMapsLoaded) {
          isGoogleMapsLoading = false;
          script.remove();
          reject(new Error("Google Maps API failed to load within timeout"));
        }
      }, 10000);
    } catch (error) {
      isGoogleMapsLoading = false;
      reject(error);
    }
  });

  return loadPromise;
}

/**
 * Check if Google Maps API is loaded
 * @returns {boolean} True if Google Maps API is loaded and ready
 */
export function isGoogleMapsReady() {
  return isGoogleMapsLoaded && window.google && window.google.maps;
}

/**
 * Get Google Maps instance (only if already loaded)
 * @returns {object|null} Google Maps API object or null if not loaded
 */
export function getGoogleMaps() {
  return isGoogleMapsReady() ? window.google : null;
}

export default {
  loadGoogleMapsAPI,
  isGoogleMapsReady,
  getGoogleMaps,
};
